import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { LabelType } from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useLabelName } from './useLabelName';

export const useChipState = (
  label?: TimesheetLabel | null,
  disabled = false
): {
  isEmptyValue: boolean;
  chipLabel?: string;
  toolTipText: string;
  isDisabled: boolean;
} => {
  const emptyTooltipMsg = useMessage('timesheetLabels.emptyTaskToolTip');
  const { formatMessage } = useIntl();
  const labelName = useLabelName(label);
  const text = useMemo(() => {
    if (label?.type === LabelType.Select && !label?.value?.idValue) {
      return null;
    }

    if (
      label?.type &&
      ![LabelType.Select, LabelType.Text, LabelType.Numeric].includes(
        label?.type
      ) &&
      label?.value?.idValue === null
    ) {
      return formatMessage(
        { id: 'labelType.NoLabel' },
        {
          Label: labelName
        }
      );
    }

    if (label?.type === LabelType.Numeric) {
      return label?.value?.numericValue?.toString();
    }

    return label?.value?.stringValue;
  }, [formatMessage, label, labelName]);
  const chipLabel = useMemo(() => {
    const textValues = text && text.split(' / ');
    const displayText =
      (label?.type === LabelType.Task
        ? textValues &&
          textValues.length > 0 &&
          textValues[textValues.length - 1].trim()
        : text) || labelName;

    return label?.type === LabelType.Task && textValues && textValues.length > 1
      ? `../${displayText}`
      : displayText;
  }, [label?.type, labelName, text]);

  const emptyTaskLabelToolTipText = useMemo(
    () =>
      disabled &&
      label &&
      [LabelType.Task, LabelType.Billing].includes(label?.type)
        ? emptyTooltipMsg
        : null,
    [disabled, label, emptyTooltipMsg]
  );

  const isDisabled = useMemo(
    () =>
      disabled &&
      label &&
      [LabelType.Task, LabelType.Billing].includes(label?.type)
        ? false
        : disabled,
    [disabled, label]
  );

  const toolTipText = useMemo(() => (text ? `${labelName}: ${text}` : ''), [
    text,
    labelName
  ]);

  return useMemo(
    () => ({
      isEmptyValue: !text,
      chipLabel,
      toolTipText: emptyTaskLabelToolTipText
        ? emptyTaskLabelToolTipText
        : toolTipText,
      isDisabled: isDisabled
    }),
    [chipLabel, text, emptyTaskLabelToolTipText, toolTipText, isDisabled]
  );
};
